'use client';

import cn from 'classnames';
import { HTMLAttributes, ReactNode } from 'react';
import { Field, FieldDescription, FieldError, FieldLabel } from '@/shared/ui/core';
import { LoadedFileItem } from '@/shared/ui/core/file-upload-field/ui/loaded-file-item';
import { FileUploadBox } from './file-upload-box';
import styles from './file-upload-field.module.scss';


export interface UploadFieldFile {
    id: string;
    name: string;
    size?: string;
    state?: 'default' | 'uploading' | 'success' | 'error';
}


export interface FileUploadFieldProps
    extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
    label?: ReactNode;
    description?: ReactNode;
    error?: ReactNode;
    required?: boolean;
    files?: UploadFieldFile[];
    hint?: ReactNode;
    disabled?: boolean;
    multiple?: boolean;
    accept?: string;
    onFilesSelect?: (files: FileList | null) => void;
    onDeleteFile?: (fileId: string) => void;
    onRetryFile?: (fileId: string) => void;
}


export function FileUploadField ({
    className,
    label,
    description,
    error,
    required,
    files = [],
    hint,
    disabled = false,
    multiple = false,
    accept,
    onFilesSelect,
    onDeleteFile,
    onRetryFile,
    ...props
}: FileUploadFieldProps) {
    const isError = Boolean(error);

    return (
        <Field className={ cn(styles.root, className) } { ...props }>
            {
                label &&
                <FieldLabel required={ required }>
                    { label }
                </FieldLabel>
            }

            <FileUploadBox
                disabled={ disabled }
                multiple={ multiple }
                accept={ accept }
                onFilesSelect={ onFilesSelect }
                error={ isError ? error : undefined }
                hint={ !isError ? hint : null }
            />

            {
                !!files.length &&
                <div className={ styles.files }>
                    {
                        files.map((file) => (
                            <LoadedFileItem
                                key={ file.id }
                                fileName={ file.name }
                                fileSize={ file.size }
                                state={ file.state }
                                onDelete={
                                    onDeleteFile ? () => onDeleteFile(file.id) : undefined
                                }
                                onRetry={
                                    onRetryFile ? () => onRetryFile(file.id) : undefined
                                }
                            />
                        ))
                    }
                </div>
            }

            {
                isError &&
                <FieldError>
                    { error }
                </FieldError>
            }

            {
                description &&
                <FieldDescription>
                    { description }
                </FieldDescription>
            }
        </Field>
    );
}
